import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Card,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@material-ui/core";
import { ExpandLess, ExpandMore } from "@material-ui/icons";

import PushNotificationService from "./Locality/Service/pushNotificationService";
import socketService from "./Locality/Service/socketService";
import PageTitle from "../../components/PageTitle/PageTitle";

export default function ParentNotifications() {
  const userDetails = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userDetail") || "{}");
    } catch {
      return {};
    }
  }, []);

  const schooleId = useMemo(() => {
    const id = userDetails?.schooleId || userDetails?.schoolId;
    return id && typeof id === "object" ? id?._id : id;
  }, [userDetails]);

  const [items, setItems] = useState([]);
  const [expandedIds, setExpandedIds] = useState({});
  const [selectedMonth, setSelectedMonth] = useState("all");

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  useEffect(() => {
    if (!schooleId) return;
    // Backend infers the parent's class from the authenticated studentId.
    PushNotificationService.listParentNotifications()
      .then((res) => {
        const data = res?.data || res;
        setItems(Array.isArray(data?.notifications) ? data.notifications : []);
      })
      .catch(() => setItems([]));
  }, [schooleId]);

  useEffect(() => {
    // Real-time updates: when school admin sends, parent sees instantly.
    socketService.joinPushNotifications({
      onNotification: (notif) => {
        setItems((prev) => {
          if (!notif || !notif._id) return prev;
          if (prev.some((x) => x._id === notif._id)) return prev;
          try {
            const raw = localStorage.getItem("parent_notifications_unread") || "0";
            const n = Number(raw);
            const next = (Number.isFinite(n) ? n : 0) + 1;
            localStorage.setItem("parent_notifications_unread", String(next));
            window.dispatchEvent(new Event("parent-notifications-unread-changed"));
          } catch {
            // ignore storage errors
          }
          return [notif, ...prev];
        });
      },
    });

    return () => {
      const socket = socketService.getSocket();
      if (socket) {
        socket.off('push-notification');
      }
    };
  }, []);

  useEffect(() => {
    // Mark as read when opening notifications page
    try {
      localStorage.setItem("parent_notifications_unread", "0");
      window.dispatchEvent(new Event("parent-notifications-unread-changed"));
    } catch {
      // ignore
    }
  }, []);

  const monthOptions = useMemo(() => {
    const uniqueMonths = Array.from(
      new Set(
        items
          .map((item) => {
            if (!item.createdAt) return null;
            const date = new Date(item.createdAt);
            if (Number.isNaN(date.getTime())) return null;
            return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
          })
          .filter(Boolean),
      ),
    ).sort((a, b) => b.localeCompare(a));

    return uniqueMonths.map((value) => {
      const [year, month] = value.split("-");
      const label = new Date(Number(year), Number(month) - 1, 1).toLocaleString(undefined, {
        month: "long",
        year: "numeric",
      });
      return { value, label };
    });
  }, [items]);

  const filteredItems = useMemo(() => {
    if (selectedMonth === "all") return items;
    return items.filter((item) => {
      if (!item.createdAt) return false;
      const date = new Date(item.createdAt);
      if (Number.isNaN(date.getTime())) return false;
      const itemMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      return itemMonth === selectedMonth;
    });
  }, [items, selectedMonth]);

  return (
    <Container maxWidth="md">
      <PageTitle title="Notifications" />
      <Card style={{ padding: 18, borderRadius: 14, border: "1px solid #e2e8f0" }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gridGap={12}>
          <Box>
            <Typography variant="h6" style={{ fontWeight: 800, color: "#0F3B62" }}>
              School Notifications
            </Typography>
            <Typography variant="body2" style={{ color: "#64748b", marginTop: 2 }}>
              Important updates from your school admin.
            </Typography>
          </Box>
          <Chip
            label={`${filteredItems.length} ${filteredItems.length === 1 ? "item" : "items"}`}
            style={{ backgroundColor: "#EEF6FF", color: "#0F3B62", fontWeight: 700 }}
          />
        </Box>

        <Box mt={2}>
          <Box mb={2} display="flex" justifyContent="flex-end">
            <FormControl variant="outlined" size="small" style={{ minWidth: 220 }}>
              <InputLabel id="notification-month-filter-label">Filter by Month</InputLabel>
              <Select
                labelId="notification-month-filter-label"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                label="Filter by Month"
              >
                <MenuItem value="all">All Months</MenuItem>
                {monthOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {filteredItems.length === 0 ? (
            <Paper
              elevation={0}
              style={{
                padding: 18,
                borderRadius: 14,
                border: "1px dashed #cbd5e1",
                background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
              }}
            >
              <Typography variant="subtitle1" style={{ fontWeight: 800, color: "#0F3B62" }}>
                {items.length === 0 ? "No notifications yet" : "No notifications for selected month"}
              </Typography>
              <Typography variant="body2" style={{ color: "#64748b", marginTop: 6 }}>
                {items.length === 0
                  ? "When your school sends a message, it will appear here."
                  : "Try selecting a different month to view older notifications."}
              </Typography>
            </Paper>
          ) : (
            filteredItems.map((n) => (
              (() => {
                const isExpanded = Boolean(expandedIds[n._id]);
                const previewText =
                  n.message && n.message.length > 220
                    ? `${n.message.slice(0, 220)}...`
                    : n.message;
                const attachments = Array.isArray(n.attachments) ? n.attachments : [];
                const legacyImageUrls = Array.isArray(n.imageUrls) ? n.imageUrls : [];
                const allAttachments =
                  attachments.length > 0
                    ? attachments
                    : legacyImageUrls.map((url) => ({ url, name: "image", mime: "image/*" }));
                const imageAttachments = allAttachments.filter((a) =>
                  String(a.mime || "").startsWith("image/"),
                );
                const fileAttachments = allAttachments.filter(
                  (a) => !String(a.mime || "").startsWith("image/"),
                );

                return (
                  <Paper
                    key={n._id}
                    elevation={0}
                    style={{
                      padding: 16,
                      borderRadius: 14,
                      border: "1px solid #e2e8f0",
                      marginBottom: 14,
                      background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
                    }}
                  >
                    <Box display="flex" alignItems="flex-start" justifyContent="space-between" gridGap={10}>
                      <Box flex={1} minWidth={0}>
                        <Typography variant="subtitle1" style={{ fontWeight: 900, color: "#0F3B62" }}>
                          {n.subject || "Notification"}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gridGap={8}>
                        <Chip
                          size="small"
                          label={n.createdAt ? new Date(n.createdAt).toLocaleString() : ""}
                          style={{ backgroundColor: "#F1F5F9", color: "#475569" }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => toggleExpanded(n._id)}
                          style={{ color: "#0F3B62" }}
                        >
                          {isExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </Box>
                    </Box>

                    <Divider style={{ marginTop: 10, marginBottom: 12 }} />

                    <Typography
                      variant="body2"
                      style={{
                        color: "#334155",
                        lineHeight: 1.75,
                        whiteSpace: "pre-wrap",
                        fontSize: 14,
                      }}
                    >
                      {isExpanded ? n.message : previewText}
                    </Typography>

                    {(imageAttachments.length > 0 || fileAttachments.length > 0) && (
                      <Box mt={2}>
                        {fileAttachments.length > 0 && (
                          <Box mb={1}>
                            {fileAttachments.map((a, index) => (
                              <Box
                                key={`${n._id}-file-${index}`}
                                mb={1}
                                p={1.25}
                                style={{
                                  border: "1px solid #e2e8f0",
                                  borderRadius: 10,
                                  background: "#ffffff",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 12,
                                }}
                              >
                                <Box minWidth={0}>
                                  <Typography
                                    variant="body2"
                                    style={{
                                      color: "#0F3B62",
                                      fontWeight: 800,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                    title={a.name}
                                  >
                                    {a.name}
                                  </Typography>
                                  <Typography variant="caption" style={{ color: "#64748b" }}>
                                    {a.mime || "file"}
                                  </Typography>
                                </Box>
                                <a href={a.url} target="_blank" rel="noopener noreferrer" style={{ color: "#2563eb", fontWeight: 700 }}>
                                  Download
                                </a>
                              </Box>
                            ))}
                          </Box>
                        )}
                        <Grid container spacing={2}>
                          {imageAttachments.map((a, index) => (
                            <Grid item xs={6} sm={4} key={`${n._id}-img-${index}`}>
                              <a href={a.url} target="_blank" rel="noopener noreferrer">
                                <img
                                  src={a.url}
                                  alt={`notification-${index + 1}`}
                                  style={{
                                    width: "100%",
                                    height: isExpanded ? 150 : 110,
                                    objectFit: "cover",
                                    borderRadius: 10,
                                    border: "1px solid #e2e8f0",
                                  }}
                                />
                              </a>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    )}

                    <Box mt={1} display="flex" justifyContent="flex-end">
                      <Typography
                        variant="caption"
                        style={{ color: "#2563eb", fontWeight: 700, cursor: "pointer" }}
                        onClick={() => toggleExpanded(n._id)}
                      >
                        {isExpanded ? "Collapse" : "Expand"}
                      </Typography>
                    </Box>

                    {isExpanded && (
                      <Box mt={2} pt={2} style={{ borderTop: "1px solid #e2e8f0" }}>
                        <Typography variant="caption" style={{ color: "#64748b", fontWeight: 700 }}>
                          Thanks & Regards,
                        </Typography>
                        <Typography variant="body2" style={{ color: "#0F3B62", fontWeight: 800, marginTop: 2 }}>
                          {n.thanksAndRegards}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                );
              })()
            ))
          )}
        </Box>
      </Card>
    </Container>
  );
}

